export default {
  hero: {
    eyebrow: "AllChinaBuy Spreadsheet",
    title: "Find Better Products with the AllChinaBuy Spreadsheet",
    subtitle:
      "Explore organized AllChinaBuy spreadsheet resources, product finds, seller information, QC guides, shipping tips, reviews and practical buying references — all in one place.",
    previewTitle: "Spreadsheet Preview",
    previewCols: {
      category: "Category",
      product: "Product",
      seller: "Seller",
      price: "Price",
      qc: "QC",
      shipping: "Shipping",
      updated: "Updated",
    },
    previewRows: {
      r1: ["Shoes", "Low-top sneakers", "Verified", "$$", "Passed", "5–12 days", "Today"],
      r2: ["Hoodies", "Oversized hoodie", "Popular", "$$", "Passed", "5–12 days", "Today"],
      r3: ["T-Shirts", "Boxy tee", "Verified", "$", "Passed", "4–10 days", "Today"],
      r4: ["Bags", "Everyday tote", "Popular", "$$", "Pending", "6–14 days", "Today"],
    },
  },
  what: {
    title: "What Is the AllChinaBuy Spreadsheet?",
    body1:
      "The AllChinaBuy Spreadsheet is a convenient way to organize and discover product resources, categories, seller information and useful buying references.",
    body2:
      "Instead of searching through scattered pages, use our structured guides and spreadsheet resources to compare products, understand QC information, research shipping options and make more informed decisions.",
  },
  categories: {
    title: "Popular AllChinaBuy Spreadsheet Categories",
    subtitle:
      "Browse organized product categories and continue to the matching pages on our main website.",
    select: "Jump to a category",
    viewAll: "View All Categories",
  },
  before: {
    title: "Everything You Need Before You Buy",
    cards: {
      review: {
        title: "AllChinaBuy Review",
        text: "Understand the platform, workflow, advantages and potential limitations.",
      },
      shipping: {
        title: "AllChinaBuy Shipping Cost",
        text: "Learn what can affect shipping costs, delivery methods and total expenses.",
      },
      warehouse: {
        title: "AllChinaBuy Warehouse",
        text: "Understand storage, QC inspection and warehouse-related considerations.",
      },
      coupon: {
        title: "AllChinaBuy Coupon",
        text: "Find information about coupons, promotions and how discount offers generally work.",
      },
      discount: {
        title: "AllChinaBuy Discount Code",
        text: "Learn how discount codes may work and what to check before using one.",
      },
      legit: {
        title: "AllChinaBuy Legit",
        text: "Understand important legitimacy, safety and verification considerations.",
      },
    },
  },
  compare: {
    title: "Compare Before You Buy",
    subtitle: "Use these comparison points to make more informed decisions.",
    headers: {
      topic: "Topic",
      check: "What to Check",
      considerations: "Important Considerations",
      bestFor: "Best For",
    },
    rows: {
      products: {
        topic: "Products",
        check: "Photos, measurements, materials",
        considerations: "Compare multiple listings before choosing",
        bestFor: "Finding the right item",
      },
      sellers: {
        topic: "Sellers",
        check: "Reviews, history, response",
        considerations: "Prefer sellers with clear track records",
        bestFor: "Reducing risk",
      },
      shipping: {
        topic: "Shipping",
        check: "Method, weight, destination",
        considerations: "Heavier items usually cost more",
        bestFor: "Planning your budget",
      },
      qc: {
        topic: "QC",
        check: "Inspection photos, measurements",
        considerations: "Request QC before shipping when possible",
        bestFor: "Catching problems early",
      },
      categories: {
        topic: "Categories",
        check: "Relevant product type",
        considerations: "Start from the right category",
        bestFor: "Faster browsing",
      },
      buying: {
        topic: "Buying methods",
        check: "Checkout flow, payment",
        considerations: "Understand the process before ordering",
        bestFor: "Smoother checkout",
      },
    },
  },
  how: {
    title: "How the AllChinaBuy Spreadsheet Works",
    steps: {
      s1: { title: "Choose a category", text: "Start from the category that matches what you want." },
      s2: { title: "Explore spreadsheet resources", text: "Review the organized product and seller references." },
      s3: { title: "Compare product and seller info", text: "Weigh options side by side before deciding." },
      s4: { title: "Check QC and buying considerations", text: "Understand what to verify before ordering." },
      s5: { title: "Research shipping and warehouse", text: "Estimate costs, delivery time and storage." },
      s6: { title: "Continue to the main website", text: "Head to our main website when you are ready to buy." },
    },
  },
  qc: {
    title: "AllChinaBuy QC & Safety Guide",
    body: "Before you order, keep these practical points in mind.",
    items: {
      photos: { title: "QC photos", text: "Review inspection photos and request extra shots when possible." },
      inspection: { title: "Product inspection", text: "Check measurements, labels and overall condition." },
      seller: { title: "Seller verification", text: "Look at seller history and feedback before buying." },
      descriptions: { title: "Product descriptions", text: "Read listings carefully to confirm size and material." },
      payment: { title: "Payment awareness", text: "Only use the official checkout process." },
      shipping: { title: "Shipping risks", text: "Understand delivery times and potential delays." },
      refund: { title: "Refund considerations", text: "Know the refund policy before you commit." },
      personal: { title: "Personal information", text: "Protect your details and avoid unverified contacts." },
      scam: { title: "Scam red flags", text: "Be cautious of deals that look too good to be true." },
    },
    read: "Read the Safety Guide",
  },
  guides: {
    title: "Latest AllChinaBuy Guides & Reviews",
    subtitle: "Explore our original guides written for practical, useful buying information.",
    card1: {
      category: "Spreadsheet Guide",
      title: "What Is the AllChinaBuy Spreadsheet? Complete 2026 Guide",
      summary: "A complete walkthrough of how the spreadsheet works, what it contains and how to use it.",
      date: "Updated 2026",
      cta: "Read Guide",
    },
    card2: {
      category: "QC & Safety",
      title: "AllChinaBuy QC & Safety Checklist",
      summary: "Practical points to verify before you buy, from QC photos to seller checks.",
      date: "Updated 2026",
      cta: "Learn More",
    },
    card3: {
      category: "Shipping",
      title: "AllChinaBuy Shipping & Cost Considerations",
      summary: "What affects shipping cost, delivery time and warehouse storage.",
      date: "Updated 2026",
      cta: "Learn More",
    },
  },
  faq: {
    title: "Frequently Asked Questions",
    items: {
      q1: {
        q: "What is an AllChinaBuy Spreadsheet?",
        a: "It is an organized resource that groups product finds, categories, sellers and buying references so you can compare options more easily before ordering through AllChinaBuy.",
      },
      q2: {
        q: "How does the spreadsheet work?",
        a: "You start from a category, explore the organized resources, compare products and sellers, check QC and shipping, then continue to the main website when ready.",
      },
      q3: {
        q: "Is AllChinaBuy legit?",
        a: "AllChinaBuy is a shopping-agent style platform used by many buyers. As with any purchase, do your own verification, check seller history and review the terms before ordering.",
      },
      q4: {
        q: "Is AllChinaBuy safe?",
        a: "Use the official checkout process, protect your personal information, and be cautious of deals that appear too good to be true.",
      },
      q5: {
        q: "How much does AllChinaBuy shipping cost?",
        a: "Shipping cost depends on weight, dimensions, method and destination. Heavier and larger items generally cost more, and faster methods usually carry a premium.",
      },
      q6: {
        q: "Does AllChinaBuy have coupons?",
        a: "Coupons and promotions can vary over time. Check the main website for current offers and always review the terms before using a code.",
      },
      q7: {
        q: "What is the AllChinaBuy warehouse?",
        a: "The warehouse is where items may be stored and inspected before they are shipped. Storage and QC options can affect your total cost and timeline.",
      },
      q8: {
        q: "How do I check QC?",
        a: "Request QC photos and review measurements, labels and condition. Compare against the listing description before approving shipment.",
      },
      q9: {
        q: "How do I compare products?",
        a: "Use the comparison points above: check photos, sellers, shipping, QC and categories side by side to make a more informed decision.",
      },
    },
  },
  finalCta: {
    title: "Ready to Explore More?",
    body: "Find organized resources, practical guides and useful buying information before making your next purchase.",
    button: "Explore Spreadsheet",
    secondary: "Visit Main Website",
  },
};